import React from "react";
import { Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import AudienceResumenHolder from "../../styles/audienceResumenStyle";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const DISPLAY_COLORS = [
  "#57c7d4",
  "#926dde",
  "#3aa99e",
  "#Fb8db4",
  "#bba7E4",
  "#9daaf3",
  "#a2caee",
  "#9ae1e9",
  "#79d1c9",
  "#f978a6",
];

const getDaysOfWeek = (t) => [
  { label: t("Sunday"), value: 0 },
  { label: t("Monday"), value: 1 },
  { label: t("Tuesday"), value: 2 },
  { label: t("Wednesday"), value: 3 },
  { label: t("Thursday"), value: 4 },
  { label: t("Friday"), value: 5 },
  { label: t("Saturday"), value: 6 },
];

const chartOptions = (title, showLegend) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: showLegend } },
  scales: {
    y: { beginAtZero: true, title: { display: true, text: title } },
    x: { beginAtZero: true },
  },
});

const lineData = (labels, dataPoints, label, color) => ({
  labels,
  datasets: [{ label, data: dataPoints, borderColor: color, borderWidth: 3 }],
});

const AudienceResumenChart = ({ proposalAudience }) => {
  const { t } = useTranslation();
  const pa = proposalAudience || {};
  const maxDay = pa.max_audience_day || {};
  const maxHour = pa.max_audience_hour || {};

  const dayObj =
    maxDay.day !== undefined ? getDaysOfWeek(t).find((d) => d.value === maxDay.day) : null;

  const daily = pa.daily_audience || [];
  const hourly = pa.hourly_audience_avg || [];
  const byDisplay = pa.daily_audience_by_display || [];

  const displaysData = byDisplay.reduce((acc, entry) => {
    if (!acc[entry.display.id]) {
      acc[entry.display.id] = { display: entry.display, dates: [], audiences: [] };
    }
    acc[entry.display.id].dates.push(entry.date);
    acc[entry.display.id].audiences.push(entry.audience);
    return acc;
  }, {});
  const displayKeys = Object.keys(displaysData);

  return (
    <AudienceResumenHolder>
      <div className="audience-graph">
        <h3>{t("Average audience by date")}</h3>
        <Row type="flex" justify="center" align="middle" gutter={16}>
          <Col xs={12} sm={6} md={6} lg={6} xl={6}>
            <div className="card-container">
              <div className="data-title">
                {dayObj && maxDay.audience ? dayObj.label : "-"}
              </div>
              {t("Day of greatest impact")}
            </div>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} xl={6}>
            <div className="card-container">
              <div className="data-title">{maxDay.audience || "-"}</div>
              {t("Biggest Impact/Day")}
            </div>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} xl={6}>
            <div className="card-container">
              <div className="data-title">{maxHour.audience ? `${maxHour.hour}:00` : "-"}</div>
              {t("Hour with the greatest impact")}
            </div>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6} xl={6}>
            <div className="card-container">
              <div className="data-title">{maxHour.audience || "-"}</div>
              {t("Biggest Impact/Hour")}
            </div>
          </Col>
        </Row>

        <Row type="flex" justify="center" gutter={16} style={{ marginTop: 10 }}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <div className="card-container">
              <h4>{t("Sum of Impressions per day (Dates)")}</h4>
              <div className="audience-graph-container">
                <Line
                  data={lineData(
                    daily.map((d) => d.date),
                    daily.map((d) => d.audience),
                    t("Sum of Impressions per day"),
                    "rgb(169, 137, 197)"
                  )}
                  options={chartOptions(t("Audience Average"), false)}
                />
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <div className="card-container">
              <h4>{t("Average Impressions per Hour (Dates)")}</h4>
              <div className="audience-graph-container">
                <Line
                  data={lineData(
                    hourly.map((h) => h.hour),
                    hourly.map((h) => h.audience),
                    t("Sum of Impressions per day"),
                    "rgb(136, 191, 77)"
                  )}
                  options={chartOptions(t("Audience"), false)}
                />
              </div>
            </div>
          </Col>
        </Row>

        {displayKeys.length > 0 && (
          <Row type="flex" justify="center" gutter={16} style={{ marginTop: 10 }}>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <div className="card-container">
                <h4>{t("Comparison by Screen")}</h4>
                <div className="audience-graph-container">
                  <Line
                    data={{
                      labels: displaysData[displayKeys[0]].dates,
                      datasets: displayKeys.map((k, index) => ({
                        label: displaysData[k].display.name,
                        data: displaysData[k].audiences,
                        borderColor: DISPLAY_COLORS[index % DISPLAY_COLORS.length],
                        borderWidth: 3,
                      })),
                    }}
                    options={chartOptions(t("Audience Average"), true)}
                  />
                </div>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </AudienceResumenHolder>
  );
};

export default AudienceResumenChart;
